<template>
    <button
        type="button"
        class="group flex w-full items-center gap-3 rounded-md px-2.5 py-4 text-start transition-colors enabled:hover:bg-surface-hover disabled:cursor-default"
        :class="{ 'bg-surface-sunken': highlighted }"
        :disabled="isRowDisabled"
        :data-highlighted="highlighted || undefined"
        :data-testid="`picker-row-${buttonState.kind}`"
        @click="$emit('row-clicked', review)">
        <span class="grow min-w-0 body-small text-ink" :class="wrapLabel ? 'break-words' : 'truncate'" :title="wrapLabel ? undefined : review.cta?.label">
            {{ review.cta?.label }}
        </span>
        <span class="flex items-center gap-3 shrink-0">
            <BaseChip size="2xs" class="justify-center" :class="{ 'min-w-24': !wrapLabel }">{{ buttonState.text }}</BaseChip>
            <!-- Always rendered so the chip keeps a stable right edge across enabled/disabled rows. -->
            <i
                class="bi bi-arrow-right w-3.5 shrink-0 text-sm/none text-ink opacity-0 -translate-x-1 transition-[opacity,translate] duration-150"
                :class="{ 'group-hover:opacity-100 group-hover:translate-x-0': !isRowDisabled }"
                aria-hidden="true"></i>
        </span>
    </button>
</template>

<script setup lang="ts">
import BaseChip from "~vue/components/BaseChip.vue";
import { isReviewCompleted } from "~vue/Moments/composables/useReviewGroupStatus";
import type { DecoratedPerformanceReview } from "~vue/types/performance_management";
import { computed } from "vue";

interface Props {
    review: DecoratedPerformanceReview;
    // Hashid of the coaching session the picker is rendered inside — its own row shows "Started".
    currentConversationHashid?: string;
    isBusy?: boolean;
    // Visual emphasis for the row Nadia preselected from the user's words.
    highlighted?: boolean;
    // Expired cycles allow View/Resume (plain navigation) but no new starts.
    startDisabled?: boolean;
    // The narrow sidebar wraps labels and lets chips shrink; the wider in-chat picker keeps one line.
    wrapLabel?: boolean;
}

const props = withDefaults(defineProps<Props>(), { currentConversationHashid: "", isBusy: false, highlighted: false, startDisabled: false, wrapLabel: false });

defineEmits<{ "row-clicked": [review: DecoratedPerformanceReview] }>();

type ButtonKind = "start" | "resume" | "started" | "view" | "marked-complete" | "unavailable";

// Clicking the row starts/resumes/views. The chip mirrors the PM page's CTA button copy
// (PerformanceManagementCTA.buttonState) so a task reads the same wherever it appears:
// Start / Continue / View / Marked complete / Unavailable.
const buttonState = computed<{ kind: ButtonKind; text: string }>(() => {
    const conversation = props.review.conversation;
    if (conversation?.coaching_session_hashid && conversation.coaching_session_hashid === props.currentConversationHashid) {
        return { kind: "started", text: "Started" };
    }
    if (isReviewCompleted(props.review)) {
        // Manually completed with no conversation to open — inert status text, as on the PM page.
        return conversation ? { kind: "view", text: "View" } : { kind: "marked-complete", text: "Marked complete" };
    }
    if (conversation?.state === "in_progress") {
        return { kind: "resume", text: "Continue" };
    }
    if (conversation) {
        return { kind: "view", text: "View" };
    }
    if (props.startDisabled) {
        return { kind: "unavailable", text: "Unavailable" };
    }
    return { kind: "start", text: "Start" };
});

const isRowDisabled = computed(
    () => props.isBusy || buttonState.value.kind === "started" || buttonState.value.kind === "marked-complete" || buttonState.value.kind === "unavailable",
);
</script>
